import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { IndianRupee, Landmark, Pencil, Plus, Trash2 } from "lucide-react";
import { papi, errText } from "../portalApi";
import { usePortal } from "../PortalApp";
import { Card, EmptyState, Field, Modal, PageHeader, Spinner, StatusBadge, fmtDate, fmtMoney, hasPerm, inputCls } from "../ui";

const STATUSES = ["Draft", "Under Review", "Approved", "Processing", "Paid", "On Hold", "Failed"];

const num = (v) => parseFloat(v) || 0;

export default function PayrollAdmin() {
  const { emp: me } = usePortal();
  const canManage = hasPerm(me, "payroll_manage");
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [summary, setSummary] = useState(null);
  const [records, setRecords] = useState(null);
  const [people, setPeople] = useState([]);
  const [compList, setCompList] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editRec, setEditRec] = useState(null);
  const [compEmp, setCompEmp] = useState(null);
  const [compForm, setCompForm] = useState(null);
  const [form, setForm] = useState({ employeeId: "", month: "", gross: "", deductions: "", incentives: "", adjustments: "", reimbursements: "", compensationType: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, r, d, c] = await Promise.all([
        papi.get("/payroll-admin/summary", { params: { month } }),
        papi.get("/payroll-admin/records", { params: { month } }),
        papi.get("/directory"),
        papi.get("/payroll-admin/compensations"),
      ]);
      setSummary(s.data);
      setRecords(r.data);
      setPeople(d.data);
      setCompList(c.data);
    } catch (err) {
      toast.error(errText(err, "Could not load payroll data."));
    }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const netPreview = useMemo(
    () => num(form.gross) - num(form.deductions) + num(form.incentives) + num(form.adjustments) + num(form.reimbursements),
    [form]
  );

  const selComp = useMemo(() => compList.find((c) => c.employeeId === form.employeeId), [compList, form.employeeId]);

  const selectEmployee = (id) => {
    const comp = compList.find((c) => c.employeeId === id);
    setForm({ ...form, employeeId: id, gross: comp?.amount ? String(comp.amount) : "", compensationType: comp?.compensationType || "" });
  };

  if (!summary || !records) return <Spinner />;

  const createRecord = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await papi.post("/payroll-admin/records", {
        employeeId: form.employeeId,
        month: form.month || month,
        gross: num(form.gross), deductions: num(form.deductions), incentives: num(form.incentives),
        adjustments: num(form.adjustments), reimbursements: num(form.reimbursements),
        compensationType: form.compensationType || undefined,
      });
      toast.success("Payroll record created as draft.");
      setCreateOpen(false);
      setForm({ employeeId: "", month: "", gross: "", deductions: "", incentives: "", adjustments: "", reimbursements: "", compensationType: "" });
      load();
    } catch (err) {
      toast.error(errText(err, "Could not create the record."));
    } finally {
      setSaving(false);
    }
  };

  const saveRecord = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await papi.patch(`/payroll-admin/records/${editRec.id}`, {
        status: editRec.status,
        paymentDate: editRec.paymentDate || "",
        referenceNumber: editRec.referenceNumber || "",
        gross: num(editRec.gross), deductions: num(editRec.deductions), incentives: num(editRec.incentives),
        adjustments: num(editRec.adjustments), reimbursements: num(editRec.reimbursements),
        locked: Boolean(editRec.locked),
      });
      toast.success("Payroll record updated.");
      setEditRec(null);
      load();
    } catch (err) {
      toast.error(errText(err, "Could not update the record."));
    } finally {
      setSaving(false);
    }
  };

  const openComp = async (empId) => {
    try {
      const r = await papi.get(`/payroll-admin/compensation/${empId}`);
      setCompForm({ ...r.data });
      setCompEmp(empId);
    } catch (err) {
      toast.error(errText(err, "Could not load compensation."));
    }
  };

  const saveComp = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await papi.put(`/payroll-admin/compensation/${compEmp}`, compForm);
      toast.success("Compensation profile updated.");
      setCompEmp(null);
      load();
    } catch (err) {
      toast.error(errText(err, "Could not save compensation."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="payroll-admin-page">
      <PageHeader title="Payroll" sub="Monthly salary and stipend processing for the organisation.">
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className={inputCls} style={{ width: "auto" }} data-testid="payroll-month-picker" aria-label="Payroll month" />
        {canManage && (
          <button onClick={() => setCreateOpen(true)} className="btn-gold !px-5 !py-2.5 !text-xs" data-testid="create-payroll-btn">
            <Plus className="h-4 w-4" aria-hidden /> New Record
          </button>
        )}
      </PageHeader>

      <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-7" data-testid="payroll-stats">
        {[
          ["Payable", summary.payableEmployees, "pay-stat-payable"],
          ["Records", summary.records, "pay-stat-records"],
          ["Draft", summary.draft, "pay-stat-draft"],
          ["Approved", summary.approved, "pay-stat-approved"],
          ["Paid", summary.paid, "pay-stat-paid"],
          ["Missing Bank", summary.missingBank, "pay-stat-missing-bank"],
          ["Total Net", fmtMoney(summary.totalNet), "pay-stat-total-net"],
        ].map(([label, val, tid]) => (
          <Card key={tid} testid={tid} className="!p-4 text-center">
            <p className="font-serif text-xl text-navy-900">{val}</p>
            <p className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
          </Card>
        ))}
      </div>

      {records.length === 0 ? (
        <EmptyState icon={IndianRupee} title="No payroll records" sub={`Nothing processed for ${month} yet.`} testid="payroll-empty" />
      ) : (
        <Card className="!p-0 overflow-hidden" testid="payroll-table-card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="payroll-table">
              <thead>
                <tr className="border-b border-navy-900/10 bg-ivory text-left">
                  {["Employee", "Month", "Type", "Gross", "Deductions", "Net", "Status", "Paid On", ""].map((h) => (
                    <th key={h} className="px-5 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {records.map((r) => (
                  <tr key={r.id} className="border-b border-navy-900/5 last:border-0 hover:bg-ivory/60" data-testid={`payroll-row-${r.id}`}>
                    <td className="px-5 py-3">
                      <p className="font-semibold text-navy-900">{r.employeeName}</p>
                      <p className="font-mono text-[11px] text-slate-400">{r.employeeCode}</p>
                    </td>
                    <td className="px-5 py-3 text-slate-600">{r.month}</td>
                    <td className="px-5 py-3 text-slate-600">{r.compensationType}</td>
                    <td className="px-5 py-3 text-slate-600">{fmtMoney(r.gross)}</td>
                    <td className="px-5 py-3 text-rose-600">{fmtMoney(r.deductions)}</td>
                    <td className="px-5 py-3 font-semibold text-navy-900">{fmtMoney(r.net)}</td>
                    <td className="px-5 py-3">
                      <StatusBadge status={r.status} />
                      {r.locked && <p className="mt-1 text-[10px] font-semibold text-slate-400">Locked</p>}
                    </td>
                    <td className="px-5 py-3 text-slate-600">{r.paymentDate ? fmtDate(r.paymentDate) : "—"}</td>
                    <td className="px-5 py-3">
                      {canManage && (
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => setEditRec({ ...r })} className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-gold/10 hover:text-gold-dark" aria-label={`Edit payroll for ${r.employeeName}`} data-testid={`edit-payroll-${r.id}`}>
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button onClick={() => openComp(r.employeeId)} className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-gold/10 hover:text-gold-dark" aria-label={`Compensation for ${r.employeeName}`} data-testid={`comp-${r.id}`}>
                            <Landmark className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card className="mt-6 !p-0 overflow-hidden" testid="compensation-overview-card">
        <div className="border-b border-navy-900/10 bg-ivory px-5 py-3.5">
          <p className="text-sm font-semibold text-navy-900">Compensation Overview — All Employees</p>
          <p className="mt-0.5 text-[11px] text-slate-500">Monthly stipend for interns, monthly compensation for others. Edit any profile from here.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="compensation-table">
            <thead>
              <tr className="border-b border-navy-900/10 text-left">
                {["Employee", "Type", "Monthly Amount", "Frequency", "Effective From", ""].map((h) => (
                  <th key={h} className="px-5 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {compList.map((c) => (
                <tr key={c.employeeId} className="border-b border-navy-900/5 last:border-0 hover:bg-ivory/60" data-testid={`comp-row-${c.code || c.employeeId}`}>
                  <td className="px-5 py-3">
                    <p className="font-semibold text-navy-900">{c.fullName}</p>
                    <p className="font-mono text-[11px] text-slate-400">{c.code}</p>
                  </td>
                  <td className="px-5 py-3 text-slate-600">{c.compensationType}</td>
                  <td className="px-5 py-3 font-semibold text-navy-900" data-testid={`comp-amount-${c.code || c.employeeId}`}>
                    {c.amount ? fmtMoney(c.amount) : <span className="font-normal italic text-slate-400">{c.notes ? "Performance-driven" : "Not set"}</span>}
                  </td>
                  <td className="px-5 py-3 text-slate-600">{c.paymentFrequency}</td>
                  <td className="px-5 py-3 text-slate-600">{c.effectiveDate ? fmtDate(c.effectiveDate) : "—"}</td>
                  <td className="px-5 py-3 text-right">
                    {canManage && (
                      <button onClick={() => openComp(c.employeeId)} className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-gold/10 hover:text-gold-dark" aria-label={`Edit compensation for ${c.fullName}`} data-testid={`comp-edit-${c.code || c.employeeId}`}>
                        <Pencil className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="New Payroll Record">
        <form onSubmit={createRecord} className="space-y-4" data-testid="payroll-create-form">
          <Field label="Employee">
            <select required className={inputCls} value={form.employeeId} onChange={(e) => selectEmployee(e.target.value)} data-testid="payroll-employee">
              <option value="">Select employee</option>
              {people.map((p) => <option key={p.id} value={p.id}>{p.fullName} ({p.employeeId})</option>)}
            </select>
          </Field>
          {form.employeeId && (
            <p className="-mt-2 text-[11px] text-slate-500" data-testid="payroll-prefill-hint">
              {selComp?.amount
                ? `Gross and type auto-filled from the ${selComp.compensationType?.toLowerCase() || "compensation"} profile (${fmtMoney(num(selComp.amount))}) — you can still edit them below.`
                : "No compensation profile set for this employee — enter the gross manually."}
            </p>
          )}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Month">
              <input type="month" className={inputCls} value={form.month || month} onChange={(e) => setForm({ ...form, month: e.target.value })} data-testid="payroll-month" />
            </Field>
            <Field label="Type">
              <select className={inputCls} value={form.compensationType} onChange={(e) => setForm({ ...form, compensationType: e.target.value })} data-testid="payroll-type">
                <option value="">Auto (by employment type)</option>
                <option>Salary</option>
                <option>Stipend</option>
                <option>Consultancy</option>
                <option>Incentive</option>
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <Field label="Gross (₹)"><input type="number" min="0" step="0.01" required className={inputCls} value={form.gross} onChange={(e) => setForm({ ...form, gross: e.target.value })} data-testid="payroll-gross" /></Field>
            <Field label="Deductions (₹)"><input type="number" min="0" step="0.01" className={inputCls} value={form.deductions} onChange={(e) => setForm({ ...form, deductions: e.target.value })} data-testid="payroll-deductions" /></Field>
            <Field label="Incentives (₹)"><input type="number" min="0" step="0.01" className={inputCls} value={form.incentives} onChange={(e) => setForm({ ...form, incentives: e.target.value })} data-testid="payroll-incentives" /></Field>
            <Field label="Adjustments (₹)"><input type="number" step="0.01" className={inputCls} value={form.adjustments} onChange={(e) => setForm({ ...form, adjustments: e.target.value })} data-testid="payroll-adjustments" /></Field>
            <Field label="Reimbursements (₹)"><input type="number" min="0" step="0.01" className={inputCls} value={form.reimbursements} onChange={(e) => setForm({ ...form, reimbursements: e.target.value })} data-testid="payroll-reimbursements" /></Field>
            <div className="flex items-end">
              <div className="w-full rounded-xl bg-gold/10 px-4 py-2.5 text-center">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Net</p>
                <p className="font-serif text-lg text-navy-900" data-testid="payroll-net-preview">{fmtMoney(netPreview)}</p>
              </div>
            </div>
          </div>
          <button type="submit" disabled={saving} className="btn-gold w-full justify-center disabled:opacity-60" data-testid="payroll-create-submit">
            {saving ? "Creating…" : "Create Draft Record"}
          </button>
        </form>
      </Modal>

      <Modal open={Boolean(editRec)} onClose={() => setEditRec(null)} title={editRec ? `Payroll — ${editRec.employeeName} · ${editRec.month}` : ""}>
        {editRec && (
          <form onSubmit={saveRecord} className="space-y-4" data-testid="payroll-edit-form">
            <Field label="Status">
              <select className={inputCls} value={editRec.status} onChange={(e) => setEditRec({ ...editRec, status: e.target.value })} data-testid="payroll-status">
                {STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <Field label="Gross (₹)"><input type="number" min="0" step="0.01" className={inputCls} value={editRec.gross} onChange={(e) => setEditRec({ ...editRec, gross: e.target.value })} data-testid="edit-payroll-gross" /></Field>
              <Field label="Deductions (₹)"><input type="number" min="0" step="0.01" className={inputCls} value={editRec.deductions} onChange={(e) => setEditRec({ ...editRec, deductions: e.target.value })} data-testid="edit-payroll-deductions" /></Field>
              <Field label="Incentives (₹)"><input type="number" min="0" step="0.01" className={inputCls} value={editRec.incentives} onChange={(e) => setEditRec({ ...editRec, incentives: e.target.value })} data-testid="edit-payroll-incentives" /></Field>
              <Field label="Adjustments (₹)"><input type="number" step="0.01" className={inputCls} value={editRec.adjustments} onChange={(e) => setEditRec({ ...editRec, adjustments: e.target.value })} data-testid="edit-payroll-adjustments" /></Field>
              <Field label="Reimbursements (₹)"><input type="number" min="0" step="0.01" className={inputCls} value={editRec.reimbursements} onChange={(e) => setEditRec({ ...editRec, reimbursements: e.target.value })} data-testid="edit-payroll-reimbursements" /></Field>
              <Field label="Payment Date"><input type="date" className={inputCls} value={editRec.paymentDate || ""} onChange={(e) => setEditRec({ ...editRec, paymentDate: e.target.value })} data-testid="edit-payroll-payment-date" /></Field>
            </div>
            <Field label="Reference Number">
              <input className={inputCls} value={editRec.referenceNumber || ""} onChange={(e) => setEditRec({ ...editRec, referenceNumber: e.target.value })} data-testid="edit-payroll-reference" />
            </Field>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input type="checkbox" checked={Boolean(editRec.locked)} onChange={(e) => setEditRec({ ...editRec, locked: e.target.checked })} className="h-4 w-4 accent-gold" data-testid="edit-payroll-locked" />
              Lock this record (only a super admin can change it afterwards)
            </label>
            <button type="submit" disabled={saving} className="btn-gold w-full justify-center disabled:opacity-60" data-testid="payroll-edit-submit">
              {saving ? "Saving…" : "Save Record"}
            </button>
          </form>
        )}
      </Modal>

      <Modal open={Boolean(compEmp)} onClose={() => setCompEmp(null)} title={compForm ? `Compensation — ${compForm.fullName}` : ""}>
        {compForm && (
          <form onSubmit={saveComp} className="space-y-4" data-testid="compensation-form">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Type">
                <select className={inputCls} value={compForm.compensationType} onChange={(e) => setCompForm({ ...compForm, compensationType: e.target.value })} data-testid="comp-type">
                  <option>Salary</option>
                  <option>Stipend</option>
                  <option>Consultancy</option>
                  <option>Incentive</option>
                </select>
              </Field>
              <Field label="Amount (₹)">
                <input className={inputCls} value={compForm.amount || ""} onChange={(e) => setCompForm({ ...compForm, amount: e.target.value })} data-testid="comp-amount" />
              </Field>
              <Field label="Effective Date">
                <input type="date" className={inputCls} value={compForm.effectiveDate || ""} onChange={(e) => setCompForm({ ...compForm, effectiveDate: e.target.value })} data-testid="comp-effective" />
              </Field>
              <Field label="Frequency">
                <select className={inputCls} value={compForm.paymentFrequency} onChange={(e) => setCompForm({ ...compForm, paymentFrequency: e.target.value })} data-testid="comp-frequency">
                  <option>Monthly</option>
                  <option>Weekly</option>
                  <option>Per Deliverable</option>
                  <option>One-Time</option>
                </select>
              </Field>
              <Field label="Account Holder">
                <input className={inputCls} value={compForm.accountHolder || ""} onChange={(e) => setCompForm({ ...compForm, accountHolder: e.target.value })} data-testid="comp-holder" />
              </Field>
              <Field label="Bank Account">
                <input className={inputCls} value={compForm.bankAccount || ""} onChange={(e) => setCompForm({ ...compForm, bankAccount: e.target.value })} data-testid="comp-account" />
              </Field>
              <Field label="IFSC">
                <input className={inputCls} value={compForm.ifsc || ""} onChange={(e) => setCompForm({ ...compForm, ifsc: e.target.value })} data-testid="comp-ifsc" />
              </Field>
              <Field label="PAN">
                <input className={inputCls} value={compForm.pan || ""} onChange={(e) => setCompForm({ ...compForm, pan: e.target.value })} data-testid="comp-pan" />
              </Field>
            </div>
            <Field label="Breakdown (optional — e.g. Basic, HRA, Special Allowance)">
              <div className="space-y-2" data-testid="comp-breakdown-editor">
                {(compForm.breakdown || []).map((row, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      className={inputCls}
                      placeholder="Component"
                      value={row.label}
                      onChange={(e) => {
                        const bd = [...compForm.breakdown];
                        bd[i] = { ...bd[i], label: e.target.value };
                        setCompForm({ ...compForm, breakdown: bd });
                      }}
                      data-testid={`comp-bd-label-${i}`}
                    />
                    <input
                      type="number"
                      min="0"
                      className={inputCls}
                      placeholder="Amount"
                      value={row.amount}
                      onChange={(e) => {
                        const bd = [...compForm.breakdown];
                        bd[i] = { ...bd[i], amount: e.target.value };
                        setCompForm({ ...compForm, breakdown: bd });
                      }}
                      data-testid={`comp-bd-amount-${i}`}
                    />
                    <button
                      type="button"
                      onClick={() => setCompForm({ ...compForm, breakdown: compForm.breakdown.filter((_, j) => j !== i) })}
                      className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-rose-50 hover:text-rose-600"
                      aria-label="Remove component"
                      data-testid={`comp-bd-remove-${i}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setCompForm({ ...compForm, breakdown: [...(compForm.breakdown || []), { label: "", amount: "" }] })}
                  className="text-xs font-semibold text-gold-dark transition-colors hover:text-navy-900"
                  data-testid="comp-bd-add"
                >
                  + Add component
                </button>
              </div>
            </Field>
            <Field label="Notes (e.g. performance-driven terms)">
              <textarea rows={2} className={inputCls} value={compForm.notes || ""} onChange={(e) => setCompForm({ ...compForm, notes: e.target.value })} data-testid="comp-notes" />
            </Field>
            <button type="submit" disabled={saving} className="btn-gold w-full justify-center disabled:opacity-60" data-testid="comp-save">
              {saving ? "Saving…" : "Save Compensation"}
            </button>
          </form>
        )}
      </Modal>
    </div>
  );
}
